import type { App, Directive, Plugin } from 'vue'
import GlobalTooltip from './GlobalTooltip.vue'
import { globalTooltip } from './state'

// 브라우저 기본 툴팁(title 속성) 대신 공용 TextTooltip으로 표시하기 위한 디렉티브.
// 사용: v-text-tooltip="'닫기'" / v-text-tooltip="item.name"
// .overflow 수식어를 붙이면 말줄임(…)으로 텍스트가 실제로 잘렸을 때만 표시한다: v-text-tooltip.overflow="item.name"
// 화면에 TextTooltip 하나(GlobalTooltip, App.vue)만 두고 모든 v-text-tooltip 요소가 공유한다.
// (Vuetify가 v-tooltip 이름을 이미 쓰고 있어 v-text-tooltip으로 등록한다.)

interface TooltipEl extends HTMLElement {
  __tooltipText?: string
  __tooltipAriaLabel?: boolean
  __tooltipOverflowOnly?: boolean
  __tooltipHandlers?: {
    enter: (e: MouseEvent) => void
    move: (e: MouseEvent) => void
    leave: () => void
  }
}

let hoveredEl: TooltipEl | null = null

function hide(): void {
  hoveredEl = null
  globalTooltip.handleTooltipMouseLeave()
}

// 아이콘만 있는 버튼은 title이 접근성 이름 역할도 했으므로, 텍스트가 없으면 aria-label로 옮겨 준다.
function syncAriaLabel(el: TooltipEl): void {
  const text = el.__tooltipText || ''
  if (el.__tooltipAriaLabel || (!el.hasAttribute('aria-label') && !el.textContent?.trim())) {
    if (text) el.setAttribute('aria-label', text)
    else el.removeAttribute('aria-label')
    el.__tooltipAriaLabel = true
  }
}

export const tooltipDirective: Directive<TooltipEl, string | null | undefined> = {
  mounted(el, binding) {
    el.__tooltipText = binding.value ?? ''
    el.__tooltipOverflowOnly = !!binding.modifiers.overflow
    const enter = (e: MouseEvent) => {
      if (!el.__tooltipText) return
      hoveredEl = el
      globalTooltip.handleTextMouseEnter(e, el.__tooltipText, !el.__tooltipOverflowOnly)
    }
    const move = (e: MouseEvent) => {
      if (hoveredEl === el) globalTooltip.handleTooltipMouseMove(e)
    }
    const leave = () => {
      if (hoveredEl === el) hide()
    }
    el.__tooltipHandlers = { enter, move, leave }
    el.addEventListener('mouseenter', enter)
    el.addEventListener('mousemove', move)
    el.addEventListener('mouseleave', leave)
    // 기본 title 툴팁처럼 클릭하면 닫는다(다이얼로그가 열려도 툴팁이 남지 않도록).
    el.addEventListener('mousedown', leave)
    syncAriaLabel(el)
  },
  updated(el, binding) {
    el.__tooltipText = binding.value ?? ''
    if (hoveredEl === el) {
      if (el.__tooltipText) globalTooltip.hoveredTooltipText.value = el.__tooltipText
      else hide()
    }
    syncAriaLabel(el)
  },
  beforeUnmount(el) {
    const h = el.__tooltipHandlers
    if (h) {
      el.removeEventListener('mouseenter', h.enter)
      el.removeEventListener('mousemove', h.move)
      el.removeEventListener('mouseleave', h.leave)
      el.removeEventListener('mousedown', h.leave)
    }
    if (hoveredEl === el) hide()
  },
}

// v-html·contenteditable처럼 디렉티브를 직접 붙일 수 없는 동적 HTML용.
// 컨테이너에 v-text-tooltip-delegate를 붙이면 내부의 [data-tooltip] 요소에 호버할 때 공용 툴팁을 띄운다.
// (예: 실험노트 멘션 배지 <span data-tooltip="...">)
interface DelegateEl extends HTMLElement {
  __tooltipDelegateHandlers?: {
    over: (e: MouseEvent) => void
    move: (e: MouseEvent) => void
    leave: () => void
  }
}

export const tooltipDelegateDirective: Directive<DelegateEl> = {
  mounted(el) {
    const over = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.('[data-tooltip]') as TooltipEl | null
      const inside = target && el.contains(target) ? target : null
      if (inside === hoveredEl) return
      if (hoveredEl) hide()
      const text = inside?.getAttribute('data-tooltip')
      if (inside && text) {
        hoveredEl = inside
        globalTooltip.handleTextMouseEnter(e, text, true)
      }
    }
    const move = (e: MouseEvent) => {
      if (hoveredEl && el.contains(hoveredEl)) globalTooltip.handleTooltipMouseMove(e)
    }
    const leave = () => {
      if (hoveredEl && el.contains(hoveredEl)) hide()
    }
    el.__tooltipDelegateHandlers = { over, move, leave }
    el.addEventListener('mouseover', over)
    el.addEventListener('mousemove', move)
    el.addEventListener('mouseleave', leave)
    el.addEventListener('mousedown', leave)
  },
  beforeUnmount(el) {
    const h = el.__tooltipDelegateHandlers
    if (h) {
      el.removeEventListener('mouseover', h.over)
      el.removeEventListener('mousemove', h.move)
      el.removeEventListener('mouseleave', h.leave)
      el.removeEventListener('mousedown', h.leave)
    }
    if (hoveredEl && el.contains(hoveredEl)) hide()
  },
}

const TooltipPlugin = {
  install(app: App) {
    app.directive('text-tooltip', tooltipDirective)
    app.directive('text-tooltip-delegate', tooltipDelegateDirective)
    app.component('GlobalTooltip', GlobalTooltip)
  },
}

export default TooltipPlugin as Plugin

declare module 'vue' {
  export interface GlobalDirectives {
    vTextTooltip: typeof tooltipDirective
    vTextTooltipDelegate: typeof tooltipDelegateDirective
  }
}
